import type { InferSelectModel, InferInsertModel } from '@edgevanta/db/drizzle';
import type { schema } from '@edgevanta/db/drizzle/client';
import type { WarehouseSyncJobStatus } from '@edgevanta/db/data-types';

export type WarehouseSyncJob = InferSelectModel<
  typeof schema.warehouseSyncJobs
>;
export type InsertWarehouseSyncJob = InferInsertModel<
  typeof schema.warehouseSyncJobs
>;

export type WarehouseSyncSession = InferSelectModel<
  typeof schema.warehouseSyncSessions
>;
export type InsertWarehouseSyncSession = InferInsertModel<
  typeof schema.warehouseSyncSessions
>;

export const TABLE_NAMES = [
  'states',
  'counties',
  'districts',
  'districtCounties',
  'globalVendors',
  'contractTypes',
  'projectTypes',
  'owners',
  'vendors',
  'lettings',
  'items',
  'collectionMappings',
  'proposals',
  'proposalCounties',
  'proposalDistricts',
  'proposalItems',
  'bids',
  'bidItems',
  'bidItemLiquidAc',
  'exceptions',
  'payEstimates',
  'payEstimateItems',
] as const;

export type TableName = (typeof TABLE_NAMES)[number];

export const GLOBAL_TABLE_NAMES: readonly TableName[] = [
  'states',
  'counties',
  'districts',
  'districtCounties',
  'globalVendors',
  // Proposal classification dims — must land BEFORE proposals, whose rows
  // carry the uids that resolve against them.
  'contractTypes',
  'projectTypes',
];

export const PER_OWNER_TABLE_NAMES = TABLE_NAMES.filter(
  (
    t,
  ): t is Exclude<
    TableName,
    | 'states'
    | 'counties'
    | 'districts'
    | 'districtCounties'
    | 'globalVendors'
    | 'contractTypes'
    | 'projectTypes'
  > => !(GLOBAL_TABLE_NAMES as readonly string[]).includes(t),
);

export const TABLE_LABELS: Record<TableName, string> = {
  states: 'States',
  counties: 'Counties',
  districts: 'Districts',
  districtCounties: 'District Counties',
  globalVendors: 'Global Vendors',
  contractTypes: 'Contract Types',
  projectTypes: 'Project Types',
  owners: 'Owners',
  vendors: 'Vendors',
  lettings: 'Lettings',
  items: 'Items',
  collectionMappings: 'Collection Mappings',
  proposals: 'Proposals',
  proposalCounties: 'Proposal Counties',
  proposalDistricts: 'Proposal Districts',
  proposalItems: 'Proposal Items',
  bids: 'Bids',
  bidItems: 'Bid Items',
  bidItemLiquidAc: 'Liquid AC Classifications',
  exceptions: 'Exceptions',
  payEstimates: 'Pay Estimates',
  payEstimateItems: 'Pay Estimate Items',
};

export type TableCounts = Record<TableName, number>;

export interface SyncedCounts {
  synced: number;
  errors: number;
}

export interface StartSyncOptions {
  ownerExternalId: string;
  /**
   * Who asked for this sync, or `null` when nobody did — the scheduler started
   * it. `warehouse_sync_jobs.user_id` is a nullable `uuid` with an FK to
   * `users.id`, so `null` is the only way to say "system"; a sentinel string
   * fails the insert with `invalid input syntax for type uuid`.
   */
  userId: string | null;
  sessionId?: string;
  forceSync?: boolean;
  tables?: TableName[];
}

export interface OrchestrationJobPayload {
  jobId: string;
  ownerExternalId: string;
  forceSync?: boolean;
  tables?: TableName[];
}

export interface TableSyncJobPayload {
  jobId: string;
  table: TableName;
  remainingTables: TableName[];
}

export interface BatchSyncJobPayload {
  jobId: string;
  table: TableName;
  offset: number;
  limit: number;
  totalBatches: number;
  remainingTables: TableName[];
}

export interface FailureCallbackPayload {
  jobId: string;
  jobType:
    | 'warehouse-orchestration'
    | 'warehouse-table-sync'
    | 'warehouse-batch'
    | 'warehouse-soft-delete'
    | 'warehouse-validation';
  table?: TableName;
  offset?: number;
  /** Sanitized error reason from QStash (not exposing implementation details) */
  errorReason?: string;
}

export interface SoftDeleteJobPayload {
  jobId: string;
  ownerExternalId: string;
}

export interface ValidationJobPayload {
  jobId: string;
  ownerExternalId: string;
}

export interface ExtensionSumValidation {
  snowflakeSum: string;
  postgresqlSum: string;
  isMatch: boolean;
}

export interface BidExtensionMismatch {
  bidExternalId: string;
  extensionTotal: string | null;
  itemsExtensionSum: string;
}

export interface BidExtensionMismatchValidation {
  snowflakeMismatches: BidExtensionMismatch[];
  postgresqlMismatches: BidExtensionMismatch[];
  isMatch: boolean;
}

export interface SyncValidationResult {
  snowflakeCounts: TableCounts;
  postgresqlCounts: TableCounts;
  mismatches: TableName[];
  extensionSum?: ExtensionSumValidation;
  bidExtensionMismatches?: BidExtensionMismatchValidation;
  isValid: boolean;
}

export interface SyncProgress {
  jobId: string;
  status: WarehouseSyncJobStatus;
  tableCounts: TableCounts | null;
  currentTable: TableName | null;
  completedTables: TableName[];
  tables: Partial<Record<TableName, TableProgress>>;
  startedAt: string;
  lastUpdated: string;
  errorMessage?: string;
}

export interface TableProgress {
  total: number;
  synced: number;
  errors: number;
  completed: boolean;
}

export interface BatchSyncResult {
  synced: number;
  errors: number;
}

export interface PostgresqlValidationCounts {
  owners: number;
  states: number;
  contractTypes: number;
  projectTypes: number;
  globalVendors: number;
  vendors: number;
  lettings: number;
  items: number;
  proposals: number;
  counties: number;
  districts: number;
  districtCounties: number;
  proposalCounties: number;
  proposalDistricts: number;
  proposalItems: number;
  bids: number;
  bidItems: number;
  bidItemLiquidAc: number;
  exceptions: number;
  payEstimates: number;
  payEstimateItems: number;
  collectionMappings: number;
}

export interface OwnerDiffDetail {
  snowflakeCounts: Partial<TableCounts>;
  postgresqlCounts: Partial<TableCounts>;
  mismatches: string[];
}

export interface DiffResult {
  ownersToSync: string[];
  ownersSkipped: string[];
  details: Record<string, OwnerDiffDetail>;
  forceSync?: boolean;
}

export interface SessionStartResult {
  sessionId: string;
  status: string;
  ownersToSync: string[];
  ownersSkipped: string[];
  currentOwnerExternalId: string | null;
  currentJobId: string | null;
  diffResults: DiffResult | null;
}
